import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TuneIcon from '@mui/icons-material/Tune';
import { Badge, Box, Button, Tooltip, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { DropdownTable } from '../../shared/types';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import CalendarFilterPopover from '../TrackerFilter/CalendarFilterPopover';
import MonthSelector from '../TrackerTable/MonthSelector';
import { getCalendarUiPalette } from './calendarPalette';
import CalendarQuickSearchAutocomplete from './CalendarQuickSearchAutocomplete';
import { CalendarDateBasis, CalendarEvent } from './types';

interface CalendarFilterConfig {
  dropdownTable: DropdownTable;
  eventHandler: any;
  setFiltersOn: (value: boolean) => void;
  disableFilter: boolean;
  filterTooltipTitle: string;
}

interface CalendarTopBarProps {
  currMonth: string;
  searchCategoryOptions: readonly string[];
  searchCategory: string;
  searchValue: string;
  searchResults: CalendarEvent[];
  isSearchLoading: boolean;
  areFiltersDisabled: boolean;
  isInProgressOnly: boolean;
  isInProgressDisabled: boolean;
  dateBasis: CalendarDateBasis;
  onSearchCategoryChange: (value: string) => void;
  onSearchValueChange: (value: string) => void;
  onSearchClear: () => void;
  onResultSelect: (event: CalendarEvent) => void;
  onToggleInProgress: () => void;
  onDateBasisChange: (value: CalendarDateBasis) => void;
  showActions: boolean;
  filterConfig: CalendarFilterConfig | null;
  onMonthChange: (month: string) => void;
  onNewEntryClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const FILTER_ICON_COLOR_BY_EVENT_TYPE = {
  MediaInteraction: 'mediaInteraction',
  EngagementLog: 'primaryLighter',
  ContentLog: 'primaryLight',
} as const;

const calendarTopBarTooltips = {
  monthSelector: 'Move between months to review calendar activity over time.',
  filters: 'Open advanced filters to narrow the calendar by the selected record types.',
  inProgress: 'Show only your entries in the current calendar view that are marked In Progress.',
  entryDate: 'View entries on the calendar by the date they were created or logged.',
  dueDate: 'View entries on the calendar by the date they are due or scheduled for.',
  clearFilters: 'Clear the active top-bar filters and return to the full calendar view.',
  addEntry: 'Create a new calendar entry in one of the active record types.',
} as const;

export default function CalendarTopBar({
  currMonth,
  searchCategoryOptions,
  searchCategory,
  searchValue,
  searchResults,
  isSearchLoading,
  areFiltersDisabled,
  isInProgressOnly,
  isInProgressDisabled,
  dateBasis,
  onSearchCategoryChange,
  onSearchValueChange,
  onSearchClear,
  onResultSelect,
  onToggleInProgress,
  onDateBasisChange,
  showActions,
  filterConfig,
  onMonthChange,
  onNewEntryClick,
}: Readonly<CalendarTopBarProps>) {
  const theme = useTheme();
  const calendarUiPalette = getCalendarUiPalette(theme);
  const topBarControlTypography = {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.2,
    letterSpacing: '0.01em',
    textTransform: 'none',
  } as const;
  const filters = useFilters();
  const filtersDispatch = useFiltersDispatch();
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);

  const filterCount = Object.keys(filters).length;
  const componentColor =
    FILTER_ICON_COLOR_BY_EVENT_TYPE[filterConfig?.eventHandler.type as keyof typeof FILTER_ICON_COLOR_BY_EVENT_TYPE] ??
    'primary';
  const activeFilterButtonColor = componentColor;

  const handleFiltersButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFiltersPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  const handleClearAllFilters = () => {
    if (!filterConfig) {
      return;
    }

    filterConfig.setFiltersOn(false);
    filtersDispatch({ type: 'setFilters', payload: {} });
  };

  const handleClearTopBarFilters = () => {
    handleClearAllFilters();
    if (isInProgressOnly) {
      onToggleInProgress();
    }
  };

  const handleDateBasisChange = (_event: React.MouseEvent<HTMLElement>, value: CalendarDateBasis | null) => {
    if (value) {
      onDateBasisChange(value);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        flexDirection: 'column',
        gap: 1.5,
        mt: 0,
        mb: 1,
        mx: 'auto',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        px: 1.5,
        py: 1.25,
        borderRadius: '8px',
        backgroundColor: calendarUiPalette.topBarControls.surfaceBackground,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
        }}
      >
        <Tooltip title={calendarTopBarTooltips.monthSelector} placement="top" enterDelay={500}>
          <Box>
            <MonthSelector
              disabled={false}
              currMonth={currMonth}
              onMonthChange={onMonthChange}
              showTodayButton={false}
              labelSx={{
                ...theme.typography.h6,
                color: calendarUiPalette.eventModal.primaryText,
              }}
            />
          </Box>
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.25,
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <CalendarQuickSearchAutocomplete
            searchCategoryOptions={searchCategoryOptions}
            searchCategory={searchCategory}
            searchValue={searchValue}
            searchResults={searchResults}
            isSearchLoading={isSearchLoading}
            onSearchCategoryChange={onSearchCategoryChange}
            onSearchValueChange={onSearchValueChange}
            onSearchClear={onSearchClear}
            onResultSelect={onResultSelect}
          />
          <Tooltip title={calendarTopBarTooltips.filters} placement="top" enterDelay={500}>
            <Box>
              <Badge sx={{ height: '32px' }} badgeContent={filterCount} color="error" invisible={filterCount === 0}>
                <Button
                  type="button"
                  onClick={handleFiltersButtonClick}
                  disabled={areFiltersDisabled || !filterConfig?.dropdownTable || Boolean(filterConfig?.disableFilter)}
                  color="whiteScale"
                  variant="outlined"
                  startIcon={<TuneIcon color={filterCount > 0 ? componentColor : 'whiteScale'} />}
                  sx={{
                    height: '32px',
                    whiteSpace: 'nowrap',
                    ...topBarControlTypography,
                  }}
                  data-cy="calendar-filter-btn"
                >
                  Filters
                </Button>
              </Badge>
            </Box>
          </Tooltip>
          <Tooltip title={calendarTopBarTooltips.inProgress} placement="top" enterDelay={500}>
            <Button
              type="button"
              color="whiteScale"
              variant="outlined"
              onClick={onToggleInProgress}
              disabled={isInProgressDisabled}
              startIcon={<TaskAltIcon color={isInProgressOnly ? componentColor : 'whiteScale'} />}
              sx={{
                height: '32px',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                ...topBarControlTypography,
              }}
              data-cy="calendar-in-progress-btn"
            >
              In Progress
            </Button>
          </Tooltip>
          <ToggleButtonGroup
            exclusive
            value={dateBasis}
            onChange={handleDateBasisChange}
            size="small"
            data-cy="calendar-date-basis-toggle"
            sx={{
              backgroundColor: calendarUiPalette.topBarControls.surfaceBackground,
              borderRadius: '4px',
              '& .MuiToggleButtonGroup-grouped': {
                height: '32px',
                px: 2,
                py: 0,
                borderColor: calendarUiPalette.topBarControls.fieldBorder,
                backgroundColor: calendarUiPalette.eventModal.neutralChipBackground,
                color: calendarUiPalette.eventModal.neutralChipText,
                ...topBarControlTypography,
                '&:hover': {
                  backgroundColor: calendarUiPalette.eventModal.neutralChipBackground,
                  color: calendarUiPalette.eventModal.neutralChipText,
                },
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: calendarUiPalette.topBarControls.surfaceBackground,
                color: calendarUiPalette.eventModal.primaryText,
                borderColor: calendarUiPalette.topBarControls.fieldBorder,
                backgroundImage: 'none',
              },
              '& .MuiToggleButton-root.Mui-selected:hover': {
                backgroundColor: calendarUiPalette.topBarControls.surfaceBackground,
                color: calendarUiPalette.eventModal.primaryText,
                borderColor: calendarUiPalette.topBarControls.fieldBorder,
              },
            }}
          >
            <Tooltip title={calendarTopBarTooltips.entryDate} placement="top" enterDelay={500}>
              <ToggleButton value="entryDate" data-cy="calendar-date-basis-entry">
                {dateBasis === 'entryDate' ? (
                  <EventIcon
                    sx={{
                      color: calendarUiPalette.eventModal.actionText,
                      fontSize: 18,
                      mr: 1,
                    }}
                  />
                ) : null}
                Entry Date
              </ToggleButton>
            </Tooltip>
            <Tooltip title={calendarTopBarTooltips.dueDate} placement="top" enterDelay={500}>
              <ToggleButton value="dueDate" data-cy="calendar-date-basis-due">
                {dateBasis === 'dueDate' ? (
                  <EventIcon
                    sx={{
                      color: calendarUiPalette.eventModal.actionText,
                      fontSize: 18,
                      mr: 1,
                    }}
                  />
                ) : null}
                Due Date
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          {filterCount > 0 || isInProgressOnly ? (
            <Tooltip title={calendarTopBarTooltips.clearFilters} placement="top" enterDelay={500}>
              <Button
                type="button"
                variant="text"
                color={activeFilterButtonColor}
                onClick={handleClearTopBarFilters}
                data-cy="calendar-filter-clear-all-btn"
                sx={{
                  height: '32px',
                  alignSelf: 'center',
                  ...topBarControlTypography,
                }}
              >
                Clear All Filters
              </Button>
            </Tooltip>
          ) : null}
          {filterConfig ? (
            <CalendarFilterPopover
              anchorEl={filterAnchorEl}
              onClose={handleFiltersPopoverClose}
              eventHandler={filterConfig.eventHandler}
              setFiltersOn={filterConfig.setFiltersOn}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flex: '0 0 auto',
            ml: 'auto',
            minWidth: 0,
            alignSelf: 'flex-start',
            '@media (max-width:900px)': {
              width: '100%',
              justifyContent: 'flex-start',
              ml: 0,
              alignSelf: 'stretch',
            },
          }}
        >
          <Tooltip title={calendarTopBarTooltips.addEntry} placement="top" enterDelay={500}>
            <Box>
              <Button
                type="button"
                onClick={onNewEntryClick}
                disabled={!showActions}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                data-cy="calendar-new-entry-btn"
                sx={{
                  minWidth: 'auto',
                  height: '32px',
                  px: 2.25,
                  py: 0,
                  whiteSpace: 'nowrap',
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: '1rem',
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: 1.5,
                  letterSpacing: 'normal',
                  textTransform: 'none',
                }}
              >
                Add New Entry
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
